'use client';

import { useState, useEffect } from 'react';

interface CostSummaryWidgetProps {
  workspaceId: string;
}

interface CostOverview {
  entities_with_costs: number;
  total_monthly_cost: number;
  infrastructure_monthly: number;
  licensing_monthly: number;
  labor_monthly: number;
  support_monthly: number;
  other_monthly: number;
}

function formatCurrency(amount: number): string {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(1)}K`;
  }
  return `$${Math.round(amount)}`;
}

export function CostSummaryWidget({ workspaceId }: CostSummaryWidgetProps) {
  const [overview, setOverview] = useState<CostOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCosts = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `/api/secure/command-center/costs?workspace_id=${workspaceId}`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch costs');
        }
        const data = await response.json();
        setOverview(data.overview);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    };

    if (workspaceId) {
      fetchCosts();
    }
  }, [workspaceId]);

  if (loading) {
    return (
      <div className="bg-surface/50 rounded-xl border border-border-muted p-4">
        <div className="flex items-center gap-2 mb-4">
          <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-sm font-medium text-foreground">Cost Overview</h3>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-8 bg-cream/10 rounded" />
          <div className="h-4 bg-cream/10 rounded w-3/4" />
        </div>
      </div>
    );
  }

  if (error || !overview) {
    return (
      <div className="bg-surface/50 rounded-xl border border-border-muted p-4">
        <div className="flex items-center gap-2 mb-4">
          <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-sm font-medium text-foreground">Cost Overview</h3>
        </div>
        <p className="text-xs text-foreground-tertiary">No cost data available</p>
      </div>
    );
  }

  const total = overview.total_monthly_cost || 0;
  const costBreakdown = [
    { label: 'Infrastructure', value: overview.infrastructure_monthly || 0, color: 'bg-blue-400' },
    { label: 'Licensing', value: overview.licensing_monthly || 0, color: 'bg-purple-400' },
    { label: 'Labor', value: overview.labor_monthly || 0, color: 'bg-green-400' },
    { label: 'Support', value: overview.support_monthly || 0, color: 'bg-yellow-400' },
    { label: 'Other', value: overview.other_monthly || 0, color: 'bg-gray-400' },
  ].filter((item) => item.value > 0);

  return (
    <div className="bg-surface/50 rounded-xl border border-border-muted p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-sm font-medium text-foreground">Cost Overview</h3>
        </div>
        <span className="text-xs text-foreground-tertiary">
          {overview.entities_with_costs} entities
        </span>
      </div>

      {/* Total */}
      <div className="mb-4">
        <div className="text-2xl font-bold text-green-400">
          {formatCurrency(total)}
        </div>
        <div className="text-xs text-foreground-tertiary">per month</div>
      </div>

      {/* Breakdown bar */}
      {total > 0 && (
        <div className="mb-3">
          <div className="h-2 rounded-full bg-cream/10 overflow-hidden flex">
            {costBreakdown.map((item, idx) => (
              <div
                key={idx}
                className={`${item.color} transition-all`}
                style={{ width: `${(item.value / total) * 100}%` }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        {costBreakdown.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${item.color}`} />
            <span className="text-foreground-tertiary">{item.label}</span>
            <span className="text-foreground-muted ml-auto">{formatCurrency(item.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CostSummaryWidget;
